<template>
  <div class="chain-strip" :class="`chain-strip--${tone}`">
    <div class="chain-strip-row">
      <!--
        Status icon spans the full strip height (spinner while running)
        — large enough to read as "working" at a glance, with the text
        lines beside it.
      -->
      <div class="chain-strip-iconwrap">
        <v-icon
          :color="iconColor"
          size="28"
          :class="{ 'chain-strip-icon--spin': spinning }"
        >{{ statusIcon }}</v-icon>
      </div>

      <div class="chain-strip-body">
        <!--
          Header: chain name + status chip + step counter. When the name
          is unknown (first "queued" event) we show the execution id so
          users still see something identifying.
        -->
        <div class="chain-strip-head">
          <span class="chain-strip-name">
            {{ progress.chain_name || `Execution #${progress.execution_id}` }}
          </span>
          <span class="chain-strip-status" :class="`chain-strip-status--${tone}`">
            {{ statusLabel }}
          </span>
          <!--
            Step counter — only shown when we know the total. When the
            entry is backed by the fallback stub (total_steps=0), hiding
            the "0/?" noise is cleaner than rendering a placeholder that
            reads broken.
          -->
          <span
            v-if="progress.progress.total_steps > 0"
            class="chain-strip-count"
          >
            {{ progress.progress.completed_steps }}/{{ progress.progress.total_steps }}
          </span>
          <!-- Turn-elapsed time, when the host embeds the strip in the
               composing indicator (replaces the "advance workflow · 201s"
               thinking label). -->
          <span v-if="elapsedLabel" class="chain-strip-elapsed">{{ elapsedLabel }}</span>
        </div>

        <!--
          Progress bar. Shown whenever we know the total, or while still
          running without a denominator (queued state). Suppressed for
          terminal states with no total so a "Done" chip doesn't render
          an empty pulsing bar that reads as still working.
        -->
        <div
          v-if="progress.progress.total_steps > 0 || !isTerminal"
          class="chain-strip-bar"
        >
          <div
            v-if="progress.progress.total_steps > 0"
            class="chain-strip-bar-fill"
            :class="`chain-strip-bar-fill--${tone}`"
            :style="{ width: `${progress.progress.percentage}%` }"
          />
          <div v-else class="chain-strip-bar-indet" />
        </div>

        <!--
          Currently-executing step, e.g. `Running "extract skills" (step
          3 of 6)` — reassures the user the chain is moving during long
          synchronous runs. Only shown while non-terminal and when the
          backend knows the step name.
        -->
        <div v-if="stepLine" class="chain-strip-step">{{ stepLine }}</div>

        <!--
          Terminal actions row: surfaces a "View output" button that
          scrolls the chat to the narrated message, plus a dismiss
          control so the user can clear the chip when they're finished
          with it.
        -->
        <div v-if="isTerminal" class="chain-strip-actions">
          <button
            v-if="progress.narrationMessageId"
            type="button"
            class="chain-strip-btn chain-strip-btn--primary"
            @click="$emit('jump-to', progress.narrationMessageId)"
          >
            <v-icon size="12" color="indigo darken-2">mdi-arrow-down</v-icon>
            View output
          </button>
          <button
            type="button"
            class="chain-strip-btn chain-strip-btn--muted"
            title="Dismiss"
            @click="$emit('dismiss', progress.execution_id)"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent } from 'vue';

export default defineComponent({
  name: 'ChainProgressStrip',
  props: {
    progress: { type: Object, required: true },
    /** Optional elapsed-time text (e.g. "201s") shown at the end of the
     * header row — used when the strip stands in for the composing
     * indicator's thinking label. */
    elapsedLabel: { type: String, default: '' },
  },
  emits: ['jump-to', 'dismiss'],
  setup(props) {
    const isTerminal = computed(() =>
      ['completed', 'failed', 'awaiting_selection', 'timed_out'].includes(
        props.progress.status,
      ),
    );

    const tone = computed(() => {
      switch (props.progress.status) {
        case 'completed': return 'done';
        case 'failed': return 'error';
        case 'awaiting_selection': return 'waiting';
        case 'timed_out': return 'muted';
        default: return 'running';
      }
    });

    const statusIcon = computed(() => {
      switch (props.progress.status) {
        case 'completed': return 'mdi-check-circle';
        case 'failed': return 'mdi-alert-circle';
        case 'awaiting_selection': return 'mdi-help-circle';
        case 'timed_out': return 'mdi-clock-outline';
        default: return 'mdi-sync';
      }
    });

    const iconColor = computed(() => {
      switch (tone.value) {
        case 'done': return 'green darken-1';
        case 'error': return 'red darken-1';
        case 'waiting': return 'amber darken-2';
        case 'muted': return 'grey darken-1';
        default: return 'indigo darken-1';
      }
    });

    const spinning = computed(
      () => props.progress.status === 'queued' || props.progress.status === 'running',
    );

    const statusLabel = computed(() => {
      switch (props.progress.status) {
        case 'queued': return 'Queued';
        case 'running': return 'Running';
        case 'completed': return 'Done';
        case 'failed': return 'Failed';
        case 'awaiting_selection': return 'Needs input';
        case 'timed_out': return 'Timed out';
        default: return props.progress.status;
      }
    });

    // `Running "extract skills" (step 3 of 6)`. The index is derived
    // from completed_steps (+1 for the step in flight) rather than sent
    // separately — both values ride the same progress event so they
    // can't drift apart.
    const stepLine = computed(() => {
      if (isTerminal.value || !props.progress.current_step) return '';
      const total = props.progress.progress?.total_steps ?? 0;
      const done = props.progress.progress?.completed_steps ?? 0;
      const suffix = total > 0 ? ` (step ${Math.min(done + 1, total)} of ${total})` : '';
      return `Running “${props.progress.current_step}”${suffix}`;
    });

    return { tone, statusIcon, iconColor, spinning, statusLabel, isTerminal, stepLine };
  },
});
</script>

<style scoped>
.chain-strip {
  border-radius: 10px;
  border: 1px solid rgb(229 231 235);
  padding: 6px 10px;
  font-size: 12px;
  background: rgb(238 242 255);
}
.chain-strip--running { background: rgb(238 242 255); border-color: rgb(199 210 254); }
.chain-strip-elapsed {
  margin-left: auto;
  font-size: 11px;
  color: rgb(107 114 128);
  white-space: nowrap;
}
.chain-strip-step {
  margin-top: 4px;
  color: rgb(67 56 202);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chain-strip--done { background: rgb(236 253 245); border-color: rgb(167 243 208); }
.chain-strip--error { background: rgb(254 242 242); border-color: rgb(252 165 165); }
.chain-strip--waiting { background: rgb(255 251 235); border-color: rgb(253 230 138); }
.chain-strip--muted { background: rgb(249 250 251); border-color: rgb(229 231 235); }

.chain-strip-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
/* Full-height status/spinner column beside the text lines. */
.chain-strip-iconwrap {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}
.chain-strip-body {
  flex: 1 1 auto;
  min-width: 0;
}
.chain-strip-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.chain-strip-name {
  font-weight: 600;
  color: rgb(31 41 55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.chain-strip-status {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 500;
  flex-shrink: 0;
}
.chain-strip-status--running { color: rgb(67 56 202); }
.chain-strip-status--done { color: rgb(4 120 87); }
.chain-strip-status--error { color: rgb(185 28 28); }
.chain-strip-status--waiting { color: rgb(180 83 9); }
.chain-strip-status--muted { color: rgb(75 85 99); }

.chain-strip-count {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 10px;
  color: rgb(107 114 128);
}

.chain-strip-bar {
  margin-top: 6px;
  height: 4px;
  border-radius: 9999px;
  background: rgb(229 231 235);
  overflow: hidden;
}
.chain-strip-bar-fill {
  height: 100%;
  transition: width 0.2s ease;
}
.chain-strip-bar-fill--running { background: rgb(99 102 241); }
.chain-strip-bar-fill--done { background: rgb(16 185 129); }
.chain-strip-bar-fill--error { background: rgb(239 68 68); }
.chain-strip-bar-fill--waiting { background: rgb(245 158 11); }
.chain-strip-bar-fill--muted { background: rgb(156 163 175); }

.chain-strip-bar-indet {
  height: 100%;
  width: 100%;
  background: rgb(165 180 252);
  animation: chain-strip-pulse 1.1s ease-in-out infinite;
}
@keyframes chain-strip-pulse {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

.chain-strip-icon--spin {
  animation: chain-strip-spin 1.1s linear infinite;
}
@keyframes chain-strip-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.chain-strip-actions {
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.chain-strip-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}
.chain-strip-btn--primary {
  background: white;
  border: 1px solid rgb(165 180 252);
  color: rgb(67 56 202);
}
.chain-strip-btn--primary:hover {
  background: rgb(238 242 255);
}
.chain-strip-btn--muted {
  margin-left: auto;
  color: rgb(107 114 128);
}
.chain-strip-btn--muted:hover {
  color: rgb(31 41 55);
}
</style>
